import { CommentForm } from "@/components/comments/CommentForm.tsx";
import { CommentInline } from "@/components/comments/CommentInline.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { PreboardingAttachmentPreviewModal } from "@/pages/preboarding/widgets/PreboardingAttachmentPreviewModal.tsx";
import {
  usePreboardingAttachTaskFile,
  usePreboardingCompleteTask,
  usePreboardingDetachTaskFile,
  usePreboardingHome,
  usePreboardingStartTask,
  usePreboardingStopTask,
} from "@/queries/preboarding/use-preboarding.query.ts";
import {
  useTaskCommentDelete,
  useTaskCommentSave,
  useTaskCommentsSearchInfinite,
} from "@/queries/employees/use-employee-tasks.query.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { ApiEntityTypes } from "@/types/common.types.ts";
import { ApiPreboardingTask } from "@/types/preboarding.types.ts";
import { ApiTaskStatus, getTaskStatus } from "@/types/task.types.ts";
import {
  Box,
  Button,
  DropdownMenuItem,
  FontAwesomeIcon,
  HtmlViewer,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  SplitButton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface PreboardingTaskModalProps extends ModalProps {
  task: ApiPreboardingTask;
}

export function PreboardingTaskModal({ task, ...props }: PreboardingTaskModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { id } = props;
  const { renderDate } = useFormatter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preexistingFileUuids] = useState(() => new Set(task.files.map((file) => file.uuid)));

  const { data: home } = usePreboardingHome();
  const liveTask = home?.tasks.find((item) => item.id === task.id) ?? task;

  const { mutate: completeTask, isPending } = usePreboardingCompleteTask();
  const { mutate: startTask, isPending: isStartPending } = usePreboardingStartTask();
  const { mutate: stopTask, isPending: isStopPending } = usePreboardingStopTask();
  const attachFile = usePreboardingAttachTaskFile();
  const detachFile = usePreboardingDetachTaskFile();

  const {
    data: commentsPages,
    hasNextPage,
    isLoading: isCommentsLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useTaskCommentsSearchInfinite(task.id, {});
  const comments = useMemo(
    () => commentsPages?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [commentsPages]
  );

  const commentSave = useTaskCommentSave();
  const commentDelete = useTaskCommentDelete();

  const description = liveTask.description?.trim() ? liveTask.description : null;

  const handleComplete = () => {
    completeTask(task.id, { onSuccess: () => close(id) });
  };

  const handleStart = () => {
    startTask(task.id);
  };

  const handleStop = () => {
    stopTask(task.id);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    attachFile.mutate(
      { taskId: task.id, formData },
      {
        onSettled: () => {
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      }
    );
  };

  const handleFileRemove = (fileUuid: string) => () => {
    detachFile.mutate({ taskId: task.id, fileUuid });
  };

  const handleCommentSave = (comment?: ApiComment) => async (text: string, fileUuids: string[]) => {
    const result = await commentSave.mutateAsync({
      taskId: task.id,
      commentId: comment?.id,
      payload: { text, file_uuids: fileUuids },
    });
    return !!result.comment;
  };

  const handleCommentDelete = (comment: ApiComment) => async () => {
    await commentDelete.mutateAsync({ taskId: task.id, commentId: comment.id });
  };

  return (
    <Modal
      {...props}
      title={t("tasks:task")}
      variant={"middle"}
      size={"lg"}
      withCloser={true}
      headerActionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <SplitButton
            variant={"primary"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-check"} />}
            onClick={handleComplete}
            isLoading={isPending}
            disabled={isStartPending || isStopPending}
            menuLabel={t("tasks:change_status")}
            menu={
              liveTask.status === ApiTaskStatus.in_progress ? (
                <DropdownMenuItem
                  title={t("tasks:mark_todo")}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-rotate-left"} />}
                  onClick={handleStop}
                />
              ) : (
                <DropdownMenuItem
                  title={t("tasks:mark_in_progress")}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-play"} />}
                  onClick={handleStart}
                />
              )
            }
          >
            {t("tasks:mark_complete")}
          </SplitButton>
        </Stack>
      }
    >
      <Stack>
        <ListItem
          emphasis={"middle"}
          preTitle={t("common:title")}
          titleSlot={<Title size={"lg"} header={liveTask.title} />}
        />
        <ListItem
          emphasis={"middle"}
          preTitle={t("common:status")}
          title={getTaskStatus(liveTask.status)}
          valueSlot={
            liveTask.date_due && (
              <Stack items={"end"}>
                <Text>{t("common:due_date")}</Text>
                <Text className={"text-secondary"}>{renderDate(liveTask.date_due)}</Text>
              </Stack>
            )
          }
        />
        {description && (
          <ListItem
            emphasis={"middle"}
            preTitle={t("common:description")}
            titleSlot={<HtmlViewer content={description} />}
          />
        )}

        <Box>
          <Title
            header={t("core:preboarding_task_attachments_title")}
            size={"xs"}
            paddingTop={true}
          />
          <input
            ref={fileInputRef}
            type={"file"}
            className={"hidden"}
            onChange={handleFileChange}
          />
          <Stack gap={"sm"} items={"start"} className={"mt-2"}>
            {liveTask.files.map((file) => (
              <FileItem
                key={`file${file.id}`}
                file={file}
                className={"w-full"}
                onOpenFile={async () => {
                  openModal(PreboardingAttachmentPreviewModal, { file });
                }}
                onRemove={
                  preexistingFileUuids.has(file.uuid) ? undefined : handleFileRemove(file.uuid)
                }
              />
            ))}
            <Button
              variant={"secondary"}
              size={"sm"}
              isLoading={attachFile.isPending}
              onClick={() => fileInputRef.current?.click()}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-paperclip"} />}
            >
              {t("common:attach")}
            </Button>
          </Stack>
        </Box>

        <Box>
          <Title header={t("common:comments")} size={"xs"} paddingTop={true} />
          <Stack gap={"lg"} className={"mt-5"}>
            <CommentForm
              entityId={task.id}
              entityType={ApiEntityTypes.task}
              onSave={handleCommentSave()}
              useMentions={false}
              useFiles={false}
              alwaysShowSubmit={true}
            />
            {isCommentsLoading ? (
              <>
                <Skeleton className={"h-15 w-auto"} />
                <Skeleton className={"h-15 w-auto"} />
                <Skeleton className={"h-15 w-auto"} />
              </>
            ) : (
              <>
                {comments.map((comment) => (
                  <CommentInline
                    key={comment.id}
                    comment={comment}
                    onEdit={handleCommentSave(comment)}
                    onDelete={handleCommentDelete(comment)}
                    useMentions={false}
                    useFiles={false}
                  />
                ))}
                {hasNextPage && (
                  <Stack direction={"horizontal"}>
                    <Button
                      size={"sm"}
                      isLoading={isFetchingNextPage}
                      onClick={async () => {
                        await fetchNextPage();
                      }}
                    >
                      {t("common:show_more")}
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
}
